import type { CrawlJobStatusDTO } from '../types/knowledge';

export type CrawlJobStatusStreamHandlers = {
  onUpdate: (status: CrawlJobStatusDTO) => void;
  onComplete?: () => void;
};

function camelCaseKey(key: string): string {
  return key.replace(/_([a-z])/g, (_, character: string) =>
    character.toUpperCase(),
  );
}

function normalizeStatusCounts(
  value: unknown,
): Array<{ count?: number | null; status?: string | null }> | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.map((entry) => {
    if (!entry || typeof entry !== 'object') {
      return entry as { count?: number | null; status?: string | null };
    }

    const record = entry as Record<string, unknown>;
    return {
      count: (record.count ?? record.Count ?? null) as number | null,
      status: (record.status ?? record.Status ?? null) as string | null,
    };
  });
}

export function normalizeCrawlJobStatusResponse(
  value: unknown,
): CrawlJobStatusDTO | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const nested =
    record.payload && typeof record.payload === 'object'
      ? record.payload
      : record.data && typeof record.data === 'object'
        ? record.data
        : value;

  if (!nested || typeof nested !== 'object') {
    return null;
  }

  const source = nested as Record<string, unknown>;
  const normalized: Record<string, unknown> = {};
  for (const [key, fieldValue] of Object.entries(source)) {
    normalized[camelCaseKey(key)] = fieldValue;
  }

  if (
    !('status' in normalized) &&
    !('crawlJobId' in normalized) &&
    !('pageStatusCounts' in normalized) &&
    !('stagingDocumentStatusCounts' in normalized) &&
    !('publishedDocumentStatusCounts' in normalized)
  ) {
    return null;
  }

  const dto: CrawlJobStatusDTO = { ...(normalized as CrawlJobStatusDTO) };

  // Only overwrite histogram fields when the payload includes a real array.
  // Sparse stream events otherwise wipe earlier staging/page counts with null.
  if (Array.isArray(normalized.pageStatusCounts)) {
    dto.pageStatusCounts = normalizeStatusCounts(
      normalized.pageStatusCounts,
    ) as CrawlJobStatusDTO['pageStatusCounts'];
  } else {
    delete dto.pageStatusCounts;
  }

  if (Array.isArray(normalized.stagingDocumentStatusCounts)) {
    dto.stagingDocumentStatusCounts = normalizeStatusCounts(
      normalized.stagingDocumentStatusCounts,
    ) as CrawlJobStatusDTO['stagingDocumentStatusCounts'];
  } else {
    delete dto.stagingDocumentStatusCounts;
  }

  if (Array.isArray(normalized.publishedDocumentStatusCounts)) {
    dto.publishedDocumentStatusCounts = normalizeStatusCounts(
      normalized.publishedDocumentStatusCounts,
    ) as CrawlJobStatusDTO['publishedDocumentStatusCounts'];
  } else {
    delete dto.publishedDocumentStatusCounts;
  }

  return dto;
}

function normalizeCrawlJobStatusDto(value: unknown): CrawlJobStatusDTO | null {
  return normalizeCrawlJobStatusResponse(value);
}

export function parseCrawlJobStatusPayload(
  payload: string,
): CrawlJobStatusDTO | null {
  const trimmed = payload.trim();
  if (trimmed === '' || trimmed === '[DONE]') {
    return null;
  }

  try {
    return normalizeCrawlJobStatusDto(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

export function isTerminalCrawlJobStatusDto(
  status: CrawlJobStatusDTO,
): boolean {
  return status.status === 'COMPLETED' || status.status === 'FAILED';
}

function emitParsedStatus(
  dto: CrawlJobStatusDTO,
  handlers: CrawlJobStatusStreamHandlers,
) {
  handlers.onUpdate(dto);
  if (isTerminalCrawlJobStatusDto(dto)) {
    handlers.onComplete?.();
  }
}

function extractCompleteJsonObject(
  text: string,
): { json: string; endIndex: number } | null {
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return { json: text.slice(0, index + 1), endIndex: index + 1 };
      }
    }
  }

  return null;
}

/** Feed incremental stream text; returns unconsumed remainder. */
export function feedCrawlJobStatusStreamBuffer(
  buffer: string,
  handlers: CrawlJobStatusStreamHandlers,
): string {
  let rest = buffer;

  while (rest.length > 0) {
    rest = rest.trimStart();
    if (rest.length === 0) {
      break;
    }

    if (rest.startsWith(':') || rest.startsWith('event:')) {
      const lineEnd = rest.indexOf('\n');
      if (lineEnd === -1) {
        break;
      }
      rest = rest.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('data:')) {
      const afterPrefix = rest.slice(5).trimStart();
      const lineEnd = afterPrefix.indexOf('\n');
      if (lineEnd === -1) {
        // No newline yet: accept only if the payload is already complete JSON,
        // otherwise keep the buffer for the next chunk (or EOF trailing parse).
        const dto = parseCrawlJobStatusPayload(afterPrefix);
        if (!dto) {
          break;
        }
        emitParsedStatus(dto, handlers);
        rest = '';
        continue;
      }
      const line = afterPrefix.slice(0, lineEnd);
      const dto = parseCrawlJobStatusPayload(line);
      if (dto) {
        emitParsedStatus(dto, handlers);
      }
      rest = afterPrefix.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('{')) {
      const extracted = extractCompleteJsonObject(rest);
      if (!extracted) {
        break;
      }

      const dto = parseCrawlJobStatusPayload(extracted.json);
      if (dto) {
        emitParsedStatus(dto, handlers);
      }
      rest = rest.slice(extracted.endIndex);
      continue;
    }

    const lineEnd = rest.indexOf('\n');
    if (lineEnd === -1) {
      break;
    }

    const dto = parseCrawlJobStatusPayload(rest.slice(0, lineEnd));
    if (dto) {
      emitParsedStatus(dto, handlers);
    }
    rest = rest.slice(lineEnd + 1);
  }

  return rest;
}
